import React, { useEffect, useState } from 'react';
import { EditFormFields, ModalEditOrganizationProps } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, Footer, InputGrid, InputGroup, ManagerWrapper, SelectGrid } from './modal-edit-organization.styles';
import { Field, Form } from 'react-final-form';
import { EditFormKeys, MutateFormFields } from '../../config/form-constants';
import { validate } from '../../lib/form-validate';
import { useNormalizeInitialValues } from '../../lib/normalize-initial-values';
import { Typography } from '@shared/ui/typography/typography';
import { CardOrganizationShortInfo } from '@entities/organization/ui/card-organization-short-info/card-organization-short-info';
import { InputText } from '@shared/ui/input-text/input-text';
import { InputNumber } from '@shared/ui/input-number/input-number';
import { useOrganizationEditPaymentMutation } from '../../api/organization-edit-api';
import { CACHE_KEY_ORGANIZATION_EDIT_PAYMENT } from '../../config/api-constants';
import { useOrganizationsDetailSharedQuery } from '@entities/organization/libs/hooks/use-organization-detail-shared-query/use-organization-detail-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { InitiatorUser } from '@features/user/initiator-user/initiator-user';
import { CatalogsListDataRequest } from '@entities/catalog/api/catalog-types';
import { DEFAULT_CATALOGS_LIST_PARAMETERS } from '@entities/catalog/config/store-constants';
import { useCatalogInfinityListQuery } from '@entities/catalog/api/catalog-api';
import { Select } from '@shared/ui/select/select';
import { useCatalogSelect } from '../../lib/use-organization-options/use-organization-options';
import { Modal } from '@shared/ui/modal/modal';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalEditOrganization: React.FC<ModalEditOrganizationProps> = ({
    isShow,
    organizationId,
    onClose,
    onAccept,
}) => {
    const [getOrganizationDetail, getOrganizationDetailResult] = useOrganizationsDetailSharedQuery(organizationId);
    const [editOrganizationPayment, editOrganizationPaymentResult] = useOrganizationEditPaymentMutation({
        fixedCacheKey: CACHE_KEY_ORGANIZATION_EDIT_PAYMENT,
    });
    const organizationDetail = getOrganizationDetailResult.data;
    const manager = organizationDetail?.manager;
    const error = getQueryError(getOrganizationDetailResult);
    const isLoadingDetail =
        getOrganizationDetailResult.isFetching ||
        getOrganizationDetailResult.isLoading ||
        getOrganizationDetailResult.isUninitialized;
    const isLoadingPayment = editOrganizationPaymentResult.isLoading;

    /* Catalogs */
    const [catalogsListParameters, setCatalogsListParameters] = useState<CatalogsListDataRequest>({
        ...DEFAULT_CATALOGS_LIST_PARAMETERS,
        take: 10,
    });

    const {
        data: catalogsList,
        isLoading: isCatalogsListLoading,
        isFetching: isCatalogsListFetching,
    } = useCatalogInfinityListQuery(catalogsListParameters);

    const {
        catalogOptions,
        catalogEmptyOption,
        isShowEmptyLoaderCatalogs,
        infinityScrollCatalogsProps,
        onSearchCatalogs,
    } = useCatalogSelect({
        catalogsData: catalogsList,
        isLoading: isCatalogsListLoading,
        isFetching: isCatalogsListFetching,
        setCatalogsListParameters,
    });

    const handleSubmit = async (fields: MutateFormFields) => {
        const result = await editOrganizationPayment({
            id: organizationId,
            bankAccountNumber: fields[EditFormKeys.BankPaymentAccount],
            bankName: fields[EditFormKeys.BankName],
            bik: fields[EditFormKeys.BankBIK],
            correspondentNumber: fields[EditFormKeys.BankCorrespondentAccount],
            catalogs: fields[EditFormKeys.Catalogs].map((catalog) => Number(catalog.value)) || [],
        });
        if (!result.error) {
            getOrganizationDetail(organizationId, false);
            onAccept();
        }
    };

    useEffect(() => {
        if (isShow && organizationId) {
            getOrganizationDetail(organizationId);
        }
    }, [organizationId, isShow]);

    const initialValues = useNormalizeInitialValues(organizationDetail);
    const formRef = useResetModalForm<EditFormFields, typeof initialValues>(isShow);

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={initialValues}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        variant='drawer'
                        size='l'
                        showLine={false}
                        header={<Typography size='h5'>Редактирование организации</Typography>}
                        footer={
                            <Footer>
                                <Button disabled={isLoadingPayment} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingPayment}
                                    disabled={isLoadingPayment}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <NetworkStateNew
                                size='s'
                                isLoading={isLoadingDetail}
                                isError={Boolean(error)}
                                isInitialized={Boolean(organizationDetail)}
                                isFiltered={false}
                                hasData={Boolean(organizationDetail)}
                                errorProps={{
                                    title: 'Ошибка загрузки организации',
                                    description: error?.message,
                                }}>
                                {organizationDetail && (
                                    <>
                                        <CardOrganizationShortInfo organization={organizationDetail} />
                                        {manager && (
                                            <ManagerWrapper>
                                                <Typography size='h6'>Менеджер</Typography>

                                                <InitiatorUser
                                                    firstName={manager?.firstName}
                                                    lastName={manager?.lastName}
                                                    middleName={manager?.middleName}
                                                    organizationPost={manager?.jobTitle}
                                                    avatarUrl={manager?.photoUrl}
                                                    tooltipData={{
                                                        department: manager?.department,
                                                        email: manager?.email,
                                                        phoneNumber: manager?.phoneNumber,
                                                    }}
                                                />
                                            </ManagerWrapper>
                                        )}
                                        <InputGroup>
                                            <Typography size='h6'>Реквизиты</Typography>
                                            <InputGrid>
                                                <Field
                                                    name={EditFormKeys.BankName}
                                                    render={({ input, meta }) => (
                                                        <InputText
                                                            label='Наименование банка'
                                                            placeholder='ВТБ'
                                                            value={input.value || ''}
                                                            error={meta.touched && meta.error}
                                                            onChange={input.onChange}
                                                        />
                                                    )}
                                                />
                                                <Field
                                                    name={EditFormKeys.BankPaymentAccount}
                                                    render={({ input, meta }) => (
                                                        <InputNumber
                                                            {...input}
                                                            label='Расчетный счет'
                                                            maxLength={20}
                                                            placeholder='00000000000000000000'
                                                            error={meta.touched && meta.error}
                                                        />
                                                    )}
                                                />
                                                <Field
                                                    name={EditFormKeys.BankCorrespondentAccount}
                                                    render={({ input, meta }) => (
                                                        <InputNumber
                                                            {...input}
                                                            label='Корр. счет'
                                                            maxLength={20}
                                                            placeholder='00000000000000000000'
                                                            error={meta.touched && meta.error}
                                                        />
                                                    )}
                                                />
                                                <Field
                                                    name={EditFormKeys.BankBIK}
                                                    render={({ input, meta }) => (
                                                        <InputNumber
                                                            {...input}
                                                            label='БИК'
                                                            maxLength={9}
                                                            placeholder='000000000'
                                                            error={meta.touched && meta.error}
                                                        />
                                                    )}
                                                />
                                            </InputGrid>
                                            <Typography size='h6'>Прикрепленные каталоги</Typography>
                                            <SelectGrid>
                                                <Field
                                                    name={EditFormKeys.Catalogs}
                                                    render={({ input, meta }) => {
                                                        return (
                                                            <Select
                                                                required
                                                                isAsync
                                                                clearable
                                                                multiselect
                                                                label='Прикрепление к каталогам'
                                                                placeholder='Выберите каталоги из списка'
                                                                isShowPreloaderInList={isShowEmptyLoaderCatalogs}
                                                                onSearch={onSearchCatalogs}
                                                                infinityScrollProps={infinityScrollCatalogsProps}
                                                                values={input.value || catalogEmptyOption}
                                                                options={catalogOptions}
                                                                error={meta.touched && meta.error}
                                                                onChange={input.onChange}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </SelectGrid>
                                        </InputGroup>
                                    </>
                                )}
                            </NetworkStateNew>
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
